import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import { stopRemixThinking } from '../../editor-commands/stop-remix-thinking';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { createApiKeyedCallbackSlot } from './editor-remix-callback-registry';
import type { CapturedEditorRemixAnimationFinishedSink } from './editor-remix-callback-registry';

/**
 * Registry keyed by the editor api object (unique per editor instance).
 * Stores the stopRemixing function so cancelRemixThinking can call it to abort
 * the in-flight AI request and trigger paired aiInteraction analytics via AbortError,
 * without needing stopRemixing to be passed through the component tree.
 *
 * Uses the shared slot so a stale cleanup cannot delete a newer registration. No current mount
 * sequence produces one (there is a single registering effect per editor), so this keeps the
 * guarantee without changing behaviour.
 */
const abortRequestSlot = createApiKeyedCallbackSlot<() => void>();

/**
 * Register the stopRemixing function for a given editor api instance.
 * Called by RemixContentComponentInner when stopRemixing becomes available.
 * Returns a cleanup function to deregister on unmount.
 */
export function registerAbortRequest(
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined,
	abortFn: () => void,
): () => void {
	return abortRequestSlot.register(api, abortFn);
}

/**
 * Read the registered abort function for a given editor api instance.
 * Returns undefined if none is registered (e.g. before the flow starts).
 */
export function getAbortRequest(
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined,
): (() => void) | undefined {
	return abortRequestSlot.get(api);
}

/**
 * This function is used to cancel the remix thinking.
 * It will
 * - toggle the ai entry points to disabled-false
 * - stop the remix thinking
 *   - reopens the modal only when the caller requests it
 *
 * Callers that need to abort an in-flight AI request (e.g. image-gen flows) are
 * responsible for calling getAbortRequest(api)?.() BEFORE calling this function.
 * This keeps the abort concern explicit at the call site rather than hidden here.
 */
export function cancelRemixThinking({
	api,
	reopenModal,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	reopenModal?: boolean;
}): void {
	// end the inline-Rovo streaming
	api?.aiExperience?.actions?.endInlineRovoStreaming?.();

	api?.aiExperience?.actions.toggleAiEntryPoints?.(false);

	if (expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)) {
		// If a pre-supplied userPrompt was cancelled (e.g. image generation flow),
		// call the onCancel callback instead of reopening the remix modal.
		const remixModalMetadata = api?.malleableUi?.sharedState.currentState()?.remixModalMetadata;
		const userPrompt = remixModalMetadata?.userPrompt;
		const onCancel = remixModalMetadata?.onCancel;
		if (onCancel && userPrompt) {
			api?.core.actions.execute(stopRemixThinking({ reopenModal: false }));
			onCancel();
			return;
		}
	}

	const isUpdate = api?.malleableUi?.sharedState.currentState()?.isUpdate ?? false;
	api?.core.actions.execute(
		stopRemixThinking({
			reopenModal:
				reopenModal ?? (fg('platform_editor_ai_remix_close_modal_on_stop') ? false : !isUpdate),
		}),
	);
}

/**
 * This function is used to handle the thinking bar complete-animation finished event.
 * It will
 * - toggle the ai entry points to disabled-false
 * - stop the remix thinking
 */
export function onRemixThinkingCompleteAnimationFinished({
	api,
	capturedAnimationFinishedSink,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	capturedAnimationFinishedSink?: CapturedEditorRemixAnimationFinishedSink;
}): void {
	if (capturedAnimationFinishedSink) {
		// The sink is the whole guard: it accepts only while this flow still holds its session, so a
		// late callback retained by flow A cannot release entry points that replacement B disabled.
		// This relies on the insertion settle running *after* completion (see `hasInsertionSucceeded`
		// in `useOnComplete`); settle first and the session is already gone, the sink declines, and
		// nothing ever re-enables the entry points.
		if (capturedAnimationFinishedSink()) {
			api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
		}
		return;
	}
	api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
	api?.core.actions.execute(stopRemixThinking());
}
